import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Activity, ExternalLink, KeyRound, Plus, RotateCw, ShieldOff, TriangleAlert } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Alternador, CampoData, Entrada, Rotulo, Selecao } from '../../componentes/formulario';
import { Botao, Cartao, Selo, type CorSelo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { ariaErro, useValidacaoLocal } from '../../componentes/validacao-local';
import { useToast } from '../../componentes/toast';
import { ErroRequisicao, get, mensagemDeErro, post } from '../../nucleo/api';
import { rotuloModulo } from '../../nucleo/rotulos';
import { useSessao } from '../../nucleo/sessao';
import { guardarChaveRecente } from './chave-recente';
import { Chips, LinhaCopiavel, ListaPaginada, Quando, corDoStatusHttp } from './comum';
import { nomeDoPapel, type ChamadaApi, type Chave, type OpcoesChave } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';

interface ChaveCriada {
  id: string;
  chave: string;
  prefixo: string;
}

const estadoDaChave = (c: Chave): 'ativa' | 'revogada' | 'expirada' => (c.estado === 'revogada' ? 'revogada' : c.expirada ? 'expirada' : 'ativa');
const COR_CHAVE: Record<'ativa' | 'revogada' | 'expirada', CorSelo> = { ativa: 'verde', revogada: 'neutro', expirada: 'laranja' };

// ---------------------------------------------------------------------------
// Criação
// ---------------------------------------------------------------------------

function FormChave({ opcoes, aoCriar }: { opcoes: OpcoesChave; aoCriar: (c: ChaveCriada) => void }) {
  const { t } = useTranslation('integracoes');
  const qc = useQueryClient();
  const [nome, setNome] = useState('');
  const [total, setTotal] = useState(false);
  const [porModulo, setPorModulo] = useState<Record<string, '' | 'leitura' | 'escrita'>>({});
  const [papel, setPapel] = useState(() => (opcoes.papeis.find((p) => p.nome.trim().toLowerCase() === 'leitura') ?? opcoes.papeis[0])?.id ?? '');
  const [expira, setExpira] = useState('');
  // Fase 2, W2b: empresa da chave (com mais de uma empresa): nasce com a atual; "todas" só para quem tem o consolidado.
  const sessao = useSessao();
  const emp = sessao.empresas;
  const [empresa, setEmpresa] = useState<string>(() => (emp?.todas ? 'todas' : (emp?.atual ?? '')));
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  const escopos = total ? ['*'] : Object.entries(porModulo).flatMap(([m, tipo]) => (tipo ? [`${m}:${tipo}`] : []));

  const criar = useMutation({
    mutationFn: () =>
      post<ChaveCriada>('/integracoes/chaves', {
        nome: nome.trim(),
        escopos,
        papel_id: papel,
        expira_em: expira ? new Date(expira).toISOString() : null,
        ...(emp && emp.opcoes.length > 1 && empresa ? { empresa_id: empresa === 'todas' ? null : empresa } : {}),
      }),
    onSuccess: async (r) => {
      guardarChaveRecente(r.chave);
      await qc.invalidateQueries({ queryKey: ['integracoes', 'chaves'] });
      aoCriar(r);
    },
    onError: (e) => {
      if (e instanceof ErroRequisicao) {
        const porCampo = e.porCampo();
        setErros(porCampo);
        // Mensagem do servidor sempre traduzida (CONTRATOS 6), nunca crua.
        setErroGeral(Object.keys(porCampo).length ? null : mensagemDeErro(e));
      } else setErroGeral(mensagemDeErro(e));
    },
  });

  const { ref: refForm } = useValidacaoLocal(erros, erroGeral);
  return (
    <form
      ref={refForm}
      id="form-chave"
      noValidate
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        setErros({});
        setErroGeral(null);
        if (!nome.trim()) return setErros({ nome: t('api.form.nomeObrigatorio') });
        if (!escopos.length) return setErros({ escopos: t('api.form.escopoObrigatorio') });
        criar.mutate();
      }}
    >
      {erroGeral && (
        <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
          {erroGeral}
        </p>
      )}
      <Rotulo id="chave-nome" rotulo={t('api.campos.nome')} obrigatorio erro={erros.nome}>
        <Entrada id="chave-nome" {...ariaErro('chave-nome', erros.nome)} maxLength={80} placeholder={t('api.form.nomeExemplo')} value={nome} onChange={(e) => setNome(e.target.value)} />
      </Rotulo>

      <fieldset className="flex min-w-0 flex-col gap-3">
        <legend className="text-sm font-medium text-tinta-2">
          {t('api.campos.escopos')}
          <span className="ml-0.5 text-acento-forte">*</span>
        </legend>
        <p className="text-xs text-tinta-3">{t('api.form.escopoAjuda')}</p>
        {erros.escopos && <p className="text-xs text-perigo">{erros.escopos}</p>}
        <div className="flex items-center justify-between gap-3 rounded-lg border border-borda bg-sup-2 px-3 py-2">
          <span className="text-sm">{t('api.form.acessoTotal')}</span>
          <Alternador rotulo={t('api.form.acessoTotal')} marcado={total} aoMudar={setTotal} invalido={!!erros.escopos} />
        </div>
        {!total && (
          <div className="grid gap-2">
            {opcoes.modulos.map((m) => (
              <div key={m.slug} className="flex items-center justify-between gap-3">
                <TextoCortado className="min-w-0 text-sm">{rotuloModulo(m.slug, m.nome)}</TextoCortado>
                <div className="w-40 shrink-0">
                  <Selecao
                    aria-label={rotuloModulo(m.slug, m.nome)}
                    vazio={t('api.form.semAcesso')}
                    opcoes={[
                      { valor: 'leitura', rotulo: t('api.form.leitura') },
                      { valor: 'escrita', rotulo: t('api.form.escrita') },
                    ]}
                    value={porModulo[m.slug] ?? ''}
                    onChange={(e) => setPorModulo((p) => ({ ...p, [m.slug]: e.target.value as '' | 'leitura' | 'escrita' }))}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </fieldset>

      <Rotulo id="chave-papel" rotulo={t('api.campos.papel')} obrigatorio erro={erros.papel_id} ajuda={t('api.form.papelAjuda')}>
        <Selecao id="chave-papel" {...ariaErro('chave-papel', erros.papel_id)} opcoes={opcoes.papeis.map((p) => ({ valor: p.id, rotulo: nomeDoPapel(t, p) }))} value={papel} onChange={(e) => setPapel(e.target.value)} />
      </Rotulo>

      {emp && emp.opcoes.length > 1 && (
        <Rotulo id="chave-empresa" rotulo={t('api.campos.empresa')} erro={erros.empresa_id} ajuda={t('api.form.empresaAjuda')}>
          <Selecao
            id="chave-empresa"
            {...ariaErro('chave-empresa', erros.empresa_id)}
            opcoes={[...emp.opcoes.map((o) => ({ valor: o.id, rotulo: o.nome })), ...(emp.consolidado ? [{ valor: 'todas', rotulo: t('api.form.empresaTodas') }] : [])]}
            value={empresa}
            onChange={(e) => setEmpresa(e.target.value)}
          />
        </Rotulo>
      )}

      <Rotulo id="chave-expira" rotulo={t('api.campos.expira')} erro={erros.expira_em} ajuda={t('api.form.expiraAjuda')}>
        <CampoData id="chave-expira" {...ariaErro('chave-expira', erros.expira_em)} type="datetime-local" value={expira} onChange={(e) => setExpira(e.target.value)} />
      </Rotulo>
    </form>
  );
}

function ChaveCriadaModal({ criada, titulo, aoFechar }: { criada: ChaveCriada | null; titulo: string; aoFechar: () => void }) {
  const { t } = useTranslation('integracoes');
  const { t: tn } = useTranslation();
  return (
    <Modal
      aberto={!!criada}
      aoFechar={aoFechar}
      titulo={titulo}
      rodape={
        <>
          <Link to="/integracoes/integracoes/mcp" onClick={aoFechar} className="inline-flex h-10 items-center rounded-lg px-4 text-sm font-medium text-tinta-2 hover:bg-sup-2 hover:text-tinta-1">
            {t('api.criada.usarMcp')}
          </Link>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.fechar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm text-tinta-2">
        <p className="flex items-start gap-2 rounded-lg border border-laranja px-3 py-2">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-laranja" aria-hidden />
          <span>{t('api.criada.aviso')}</span>
        </p>
        {criada && <LinhaCopiavel texto={criada.chave} />}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Lista de chaves
// ---------------------------------------------------------------------------

function LinhaChave({ c, aoCriada, aoVerChamadas }: { c: Chave; aoCriada: (r: ChaveCriada) => void; aoVerChamadas: () => void }) {
  const { t } = useTranslation('integracoes');
  const toast = useToast();
  const qc = useQueryClient();
  const s = useSessao();
  const [confirmar, setConfirmar] = useState<'revogar' | 'rotacionar' | null>(null);
  const estado = estadoDaChave(c);
  const invalidar = () => qc.invalidateQueries({ queryKey: ['integracoes', 'chaves'] });

  const revogar = useMutation({
    mutationFn: () => post(`/integracoes/chaves/${c.id}/revogar`),
    onSuccess: async () => {
      setConfirmar(null);
      await invalidar();
      toast.sucesso(t('api.toast.revogada'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const rotacionar = useMutation({
    mutationFn: () => post<ChaveCriada>(`/integracoes/chaves/${c.id}/rotacionar`),
    onSuccess: async (r) => {
      setConfirmar(null);
      guardarChaveRecente(r.chave);
      await invalidar();
      aoCriada(r);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const pode = s.pode('integracoes.api');

  return (
    <Cartao>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-semibold" title={c.nome}>{c.nome}</h3>
          <p className="numero mt-0.5 text-xs text-tinta-3">{c.prefixo}_...</p>
        </div>
        <Selo cor={COR_CHAVE[estado]}>{t(`api.estado.${estado}`)}</Selo>
      </div>
      <div className="mt-3 flex flex-col gap-2 text-sm">
        <Chips itens={c.escopos.map((e) => (e === '*' ? t('api.campos.escopoTotal') : e))} max={6} />
        {c.assistente_id && <p className="text-xs text-tinta-3">{t('api.campos.assistenteAjuda')}</p>}
        <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
          <div className="flex justify-between gap-2">
            <dt className="text-tinta-3">{t('api.campos.papel')}</dt>
            {/* Fase 2 (W11): chave de agente não tem papel próprio (vale o do dono). */}
            <TextoCortado como="dd" className="min-w-0 text-tinta-2">{c.assistente_id ? t('api.campos.assistenteDe', { dono: c.dono_nome ?? '' }) : c.papel_nome ? nomeDoPapel(t, { nome: c.papel_nome }) : '-'}</TextoCortado>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-tinta-3">{t('api.campos.expira')}</dt>
            <dd>{c.expira_em ? <Quando v={c.expira_em} /> : <span className="text-tinta-2">{t('api.campos.naoExpira')}</span>}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-tinta-3">{t('api.campos.ultimoUso')}</dt>
            <dd>
              <Quando v={c.ultimo_uso} />
            </dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-tinta-3">{t('api.campos.criadaEm')}</dt>
            <dd>
              <Quando v={c.created_at} />
            </dd>
          </div>
        </dl>
      </div>
      <div className="mt-4 flex flex-wrap gap-2 border-t border-borda pt-4">
        <Botao icone={<Activity className="size-4" aria-hidden />} onClick={aoVerChamadas}>
          {t('api.acoes.chamadas')}
        </Botao>
        {pode && estado !== 'revogada' && (
          <>
            {!c.assistente_id && (
              <Botao icone={<RotateCw className="size-4" aria-hidden />} onClick={() => setConfirmar('rotacionar')}>
                {t('api.acoes.rotacionar')}
              </Botao>
            )}
            <Botao variante="perigo" icone={<ShieldOff className="size-4" aria-hidden />} onClick={() => setConfirmar('revogar')}>
              {t('api.acoes.revogar')}
            </Botao>
          </>
        )}
      </div>
      <Confirmar
        aberto={confirmar !== null}
        aoFechar={() => setConfirmar(null)}
        perigoso={confirmar === 'revogar'}
        carregando={revogar.isPending || rotacionar.isPending}
        aoConfirmar={() => (confirmar === 'revogar' ? revogar.mutate() : rotacionar.mutate())}
        titulo={confirmar === 'revogar' ? t('api.confirmar.revogar') : t('api.confirmar.rotacionar')}
        texto={confirmar === 'revogar' ? t('api.confirmar.revogarTexto') : t('api.confirmar.rotacionarTexto')}
      />
    </Cartao>
  );
}

export function Api() {
  const { t } = useTranslation('integracoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const [criando, setCriando] = useState(false);
  const [criada, setCriada] = useState<{ chave: ChaveCriada; rotacionada: boolean } | null>(null);
  const [filtro, setFiltro] = useState('');
  const chaves = useQuery({ queryKey: ['integracoes', 'chaves'], queryFn: () => get<{ itens: Chave[] }>('/integracoes/chaves') });
  const opcoes = useQuery({ queryKey: ['integracoes', 'chaves', 'opcoes'], queryFn: () => get<OpcoesChave>('/integracoes/chaves/opcoes'), enabled: criando });
  const base = `${window.location.origin}/api/v1`;

  return (
    <div className="flex flex-col gap-4">
      {s.pode('integracoes.api') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" aria-hidden />} onClick={() => setCriando(true)}>
            {t('api.acoes.nova')}
          </Botao>
        </AcaoPrimaria>
      )}

      <Cartao titulo={t('api.usoTitulo')}>
        <div className="flex min-w-0 flex-col gap-3 text-sm text-tinta-2">
          <p>{t('api.intro')}</p>
          <LinhaCopiavel texto={base} />
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <span className="text-xs text-tinta-3">{t('api.limite')}</span>
            <a href="/api/docs" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-acento-forte hover:underline">
              {t('api.docs')}
              <ExternalLink className="size-4" aria-hidden />
            </a>
          </div>
        </div>
      </Cartao>

      <Estado consulta={chaves}>
        {(d) =>
          d.itens.length === 0 ? (
            <div className="rounded-xl border border-borda bg-sup-1">
              <Vazio icone={<KeyRound className="size-5" aria-hidden />} titulo={t('api.vazioTitulo')} texto={t('api.vazio')} />
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {d.itens.map((c) => (
                <LinhaChave key={c.id} c={c} aoCriada={(r) => setCriada({ chave: r, rotacionada: true })} aoVerChamadas={() => setFiltro(c.id)} />
              ))}
            </div>
          )
        }
      </Estado>

      <Cartao
        titulo={t('api.chamadas.titulo')}
        semPadding
        acao={
          <div className="w-52">
            <Selecao
              aria-label={t('api.chamadas.chave')}
              vazio={t('api.chamadas.todas')}
              opcoes={(chaves.data?.itens ?? []).map((c) => ({ valor: c.id, rotulo: c.nome }))}
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
            />
          </div>
        }
      >
        <ListaPaginada<ChamadaApi>
          chaveConsulta={['integracoes', 'chamadas-api']}
          caminho="/integracoes/chamadas-api"
          params={{ chave_id: filtro || undefined }}
          vazio={t('api.chamadas.vazio')}
          colunas={[
            { chave: 'rota', rotulo: t('api.chamadas.rota'), render: (c) => <span className="font-mono text-xs">{`${c.metodo} ${c.rota}`}</span> },
            { chave: 'status', rotulo: t('api.chamadas.status'), render: (c) => <Selo cor={corDoStatusHttp(c.status)}>{c.status}</Selo> },
            { chave: 'latencia', rotulo: t('api.chamadas.latencia'), render: (c) => <span className="numero">{`${c.latencia_ms} ms`}</span> },
            { chave: 'chave', rotulo: t('api.chamadas.chave'), render: (c) => <span className="text-xs">{c.chave_nome ?? c.chave_prefixo ?? '-'}</span> },
            { chave: 'ip', rotulo: t('api.chamadas.ip'), render: (c) => <span className="numero text-xs">{c.ip ?? '-'}</span> },
            { chave: 'quando', rotulo: t('api.chamadas.quando'), render: (c) => <Quando v={c.created_at} /> },
          ]}
        />
      </Cartao>

      <Drawer
        aberto={criando}
        aoFechar={() => setCriando(false)}
        titulo={t('api.acoes.nova')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setCriando(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-chave">
              {t('api.acoes.criar')}
            </Botao>
          </>
        }
      >
        {opcoes.data && (
          <FormChave
            opcoes={opcoes.data}
            aoCriar={(r) => {
              setCriando(false);
              setCriada({ chave: r, rotacionada: false });
            }}
          />
        )}
      </Drawer>
      <ChaveCriadaModal criada={criada?.chave ?? null} titulo={criada?.rotacionada ? t('api.criada.tituloRotacionada') : t('api.criada.titulo')} aoFechar={() => setCriada(null)} />
    </div>
  );
}
